import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { stageRoute } from '../constants/stage-routes';
import { DeviceService } from '../services/device.service';
import { LocationPermissionStatus } from '../services/location.service';
import { SecureDataService } from '../services/securedata.sevice';
import { SpinnerService } from '../services/spinner.service';
import { StateService } from '../services/state.service';

/**
 * Browser history (Back/Forward, also after a reload) can open a stage page the agent no longer has
 * open, with no current status loaded, so submit and cancel fail with "Current Status is not defined"
 * (SARAPLUS-SECUREDATA-X). For history navigations, and whenever no status is loaded, re-read the
 * current status and send the customer to the page it names. Navigations the app makes after reading
 * the status pass through unchanged.
 */
@Injectable({
    providedIn: 'root'
})
export class StageStatusService {
    #router = inject(Router);
    #stateService = inject(StateService);
    #secureDataService = inject(SecureDataService);
    #deviceService = inject(DeviceService);
    #spinnerService = inject(SpinnerService);

    async canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
        // First load of the link: AppComponent reads the status and routes once the query params are set.
        if (!this.#stateService.ConnectionId) {
            return true;
        }

        const fromHistory = this.#router.getCurrentNavigation()?.trigger === 'popstate';
        if (!fromHistory && this.#stateService.CurrentStatus) {
            return true;
        }

        const page = route.routeConfig?.path;
        this.addBreadcrumb('stage_status_reload', { page, fromHistory });
        this.#spinnerService.setLoading(true, '@StageStatusGuard');
        try {
            const status = await firstValueFrom(this.#secureDataService.getStatus());
            if (!status) {
                this.addBreadcrumb('stage_status_redirect', { page, to: 'waiting' });
                return this.urlTree('waiting');
            }

            const locationStatus = status.LocationValidationStatus || 'OutsideUnitedStates';
            if (locationStatus !== 'Allowed' && this.#deviceService.locationState()?.status !== LocationPermissionStatus.Granted) {
                this.addBreadcrumb('stage_status_redirect', { page, to: 'location-required', type: status.Type });
                return this.urlTree('location-required');
            }

            const target = stageRoute(status.Type) ?? 'waiting';
            if (target === page) {
                return true;
            }
            this.addBreadcrumb('stage_status_redirect', { page, to: target, type: status.Type });
            return this.urlTree(target);
        } catch (err) {
            // HttpErrorInterceptor already shows the error, including the expired-link message.
            if ((err as HttpErrorResponse)?.status === 410 || (err as Error)?.message?.startsWith('HTTP 410 ')) {
                return this.urlTree('expired');
            }
            this.addBreadcrumb('stage_status_failed', { page });
            return false;
        } finally {
            this.#spinnerService.setLoading(false, '@StageStatusGuard');
        }
    }

    private urlTree(path: string): UrlTree {
        return this.#router.createUrlTree([path], {
            queryParams: { id: this.#stateService.ConnectionId, t: this.#stateService.MethodType }
        });
    }

    private addBreadcrumb(message: string, data: Record<string, unknown>): void {
        Sentry.addBreadcrumb({ category: 'secure-data-stage', message, data, level: 'info' });
    }
}

export const StageStatusGuard: CanActivateFn = (next: ActivatedRouteSnapshot) => {
    return inject(StageStatusService).canActivate(next);
}
